import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import type { CloseDialogHandler } from "../../contexts/DialogContext";
import { useUpdateService } from "../../hooks/mutation/update/useUpdateService";
import { useDashboard } from "../../hooks/query/useDashboard";
import { useServiceMap } from "../../hooks/query/useServiceMap";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = z.object({
  label: z.string().trim().min(1, "Service name is required"),
  owner: z.string().trim().min(1, "Owner is required"),
  slaTarget: z.number().min(0, "Minimum is 0").max(100, "Maximum is 100"),
  revenueWeight: z.number().min(0, "Minimum is 0").max(100, "Maximum is 100"),
  businessCriticality: z.number().min(0, "Minimum is 0").max(100, "Maximum is 100"),
});

type EditServiceValues = z.infer<typeof schema>;

const clamp = (value: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return min;
  }
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
};

const ratioToPercent = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (value <= 1) {
    return Math.round(clamp(value, 0, 1) * 100);
  }
  return Math.round(clamp(value, 0, 100));
};

const percentToRatio = (value: number): number => {
  return clamp(value, 0, 100) / 100;
};

const EditServiceDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  dashboardId: string;
  serviceId: string;
}> = ({ onDialogClosed, dashboardId, serviceId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const { data: dashboard } = useDashboard(dashboardId);
  const serviceMapId = dashboard?.id;
  const { data: serviceMap = { nodes: [], edges: [] } } = useServiceMap(serviceMapId);

  const serviceNode = React.useMemo(() => {
    return serviceMap.nodes.find((node) => node.id === serviceId);
  }, [serviceId, serviceMap.nodes]);

  const service = serviceNode?.data;
  const { mutateAsync: updateService, isPending: isUpdating } = useUpdateService();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditServiceValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      label: "",
      owner: "",
      slaTarget: 0,
      revenueWeight: 0,
      businessCriticality: 0,
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    if (!service) {
      return;
    }

    reset({
      label: service.label,
      owner: service.owner,
      slaTarget: Math.round(clamp(service.sla, 0, 100)),
      revenueWeight: ratioToPercent(service.revenueWeight),
      businessCriticality: ratioToPercent(service.businessCritical),
    });
  }, [reset, service]);

  const onSubmit = async (data: EditServiceValues) => {
    try {
      if (!serviceMapId) {
        setSubmitError("Service map not found.");
        return;
      }

      setSubmitError(null);
      await updateService({
        serviceMapId,
        systemId: serviceId,
        system: {
          label: data.label.trim(),
          owner: data.owner.trim(),
          sla: clamp(data.slaTarget, 0, 100),
          revenueWeight: percentToRatio(data.revenueWeight),
          businessCritical: percentToRatio(data.businessCriticality),
        },
      });

      onDialogClosed({
        message: "Service updated!",
        severity: "success",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    if (isUpdating) {
      return;
    }
    setSubmitError(null);
    onDialogClosed();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg },
      }}
    >
      <StandardDialogTitle
        title="Edit Business Service"
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box component="form" id="edit-service-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Update service details and health-impact weights.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}

              <FormControl>
                <FormLabel>Service name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Customer Checkout API"
                  error={!!errors.label}
                  {...register("label", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.label}>
                  {errors.label?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Owner</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Payments Team"
                  error={!!errors.owner}
                  {...register("owner", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.owner}>
                  {errors.owner?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>SLA target (%)</FormLabel>
                  <TextField
                    size="small"
                    type="number"
                    error={!!errors.slaTarget}
                    inputProps={{ min: 0, max: 100 }}
                    {...register("slaTarget", {
                      valueAsNumber: true,
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.slaTarget}>
                    {errors.slaTarget?.message ?? " "}
                  </FormHelperText>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <Tooltip title="Percentage of business importance. Higher values increase service health impact on overall business metrics.">
                    <FormLabel>Revenue weight (%)</FormLabel>
                  </Tooltip>
                  <TextField
                    size="small"
                    type="number"
                    error={!!errors.revenueWeight}
                    inputProps={{ min: 0, max: 100 }}
                    {...register("revenueWeight", {
                      valueAsNumber: true,
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.revenueWeight}>
                    {errors.revenueWeight?.message ?? " "}
                  </FormHelperText>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Business criticality (%)</FormLabel>
                  <TextField
                    size="small"
                    type="number"
                    error={!!errors.businessCriticality}
                    inputProps={{ min: 0, max: 100 }}
                    {...register("businessCriticality", {
                      valueAsNumber: true,
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.businessCriticality}>
                    {errors.businessCriticality?.message ?? " "}
                  </FormHelperText>
                </FormControl>
              </Stack>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText="Save"
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isUpdating}
          disabled={!service || isUpdating}
        />
      </Box>
    </Dialog>
  );
};

export default EditServiceDialog;
